import { getBuildByPatchInfo } from 'web/data/core-data'
import { FightPlayer, Spell } from 'web/data/models'
import { assertDefined } from 'web/utils/utils'

export type CooldownResolver = (spell: Spell, player: FightPlayer) => number

/**
 * Used when we need to call multiple resolves in a chain.
 */
export type DecoratingCooldownResolver = (spell: Spell, player: FightPlayer, cooldown: number) => number


/**
 * Fallback resolver when a spell is known not to have any modifiers
 */
export const emptyResolver: CooldownResolver = (spell) => {
    return spell.cooldown
}

/**
 * Returns the cooldown duration of the given spell after applying all
 * modifications found on the given player.
 *
 * @returns Cooldown duration in seconds
 */
export function resolveSpellCooldown(spellId: string, player: FightPlayer): number {
    const build = getBuildByPatchInfo(player.patchInfo)

    const { specId } = player
    const spell = build.Spell(spellId)

    const resolverFn = build.__CooldownResolver(spellId)

    if (resolverFn == null) {
        throw Error(`No resolver function defined for specId ${specId}`)
    }

    const resolvedCd = overrideResolver(
        spell,
        player,
        resolverFn
    )

    // Cap at 1 to ensure we don't brick everything downstream by
    // sending in a 0
    return Math.max(1, resolvedCd)
}

/**
 * Applies the list of resolvers to figure out the final cooldown.
 */
export function applyResolvers(params: {
    spell: Spell
    player: FightPlayer
    cooldown: number
    resolvers: { [key: string]: DecoratingCooldownResolver }
}): number {
    const { spell, player, cooldown, resolvers } = params

    const finalCooldown = Object.entries(resolvers).reduce((prev, [modifierId, resolverFn]) => {

        const spellMod = spell.affectedByModifierIds.includes(modifierId)
        const playerMod = player.modifierIds.includes(modifierId)

        if (!spellMod || !playerMod) {
            return prev
        }

        return resolverFn(spell, player, prev)

    }, cooldown)

    return finalCooldown
}

/**
 * This resolver handles spell overrides. It checks if a given spell has an
 * override for the given player, applies that override and propagates the spell
 * to its resolver.
 *
 * This resolver should always wrap all the build-specific resolvers.
 */
const overrideResolver = (spell: Spell, player: FightPlayer, spellResolver?: CooldownResolver): number => {
    assertDefined(spellResolver)

    const maybeOverride = player.overrides.find(override => override.spellId === spell.id)

    if (maybeOverride) {

        if (maybeOverride.ignoreModifiers) {
            return maybeOverride.cooldown
        }

        return spellResolver(
            {
                ...spell,
                cooldown: maybeOverride.cooldown
            },
            player
        )
    }

    return spellResolver(spell, player)
}
